import React, { useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowRight } from "lucide-react";
import { Img } from "../common/Img";
import { IMAGES } from "../../data/images";
import { HERO_VIDEO_PATH } from "../../config/media";
import { useLanguage } from "../../context/LanguageContext";
import { maskLine, EASE } from "../../lib/animations";

export default function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const lenis = useLenis();
  const reduceMotion = useReducedMotion();
  const [videoReady, setVideoReady] = useState(false);
  const [videoUnavailable, setVideoUnavailable] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", reduceMotion ? "0%" : "18%"]
  );
  const scale = useTransform(
    scrollYProgress,
    [0, 1],
    [1, reduceMotion ? 1 : 1.08]
  );
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  const lines = [t("hero.titleLine1"), t("hero.titleLine2"), t("hero.titleLine3")];

  const scrollTo = (href) => {
    const element = document.querySelector(href);
    if (element && lenis) lenis.scrollTo(element, { offset: -72 });
    else if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      ref={ref}
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-black"
      data-testid="hero-section"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <Img
          src={IMAGES.hero}
          alt={t("hero.videoFallbackAlt")}
          priority
          className="absolute inset-0 h-full w-full object-cover"
        />

        {!reduceMotion && !videoUnavailable && (
          <video
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
              videoReady ? "opacity-100" : "opacity-0"
            }`}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={IMAGES.hero}
            aria-hidden="true"
            onCanPlay={() => setVideoReady(true)}
            onError={() => setVideoUnavailable(true)}
          >
            <source src={HERO_VIDEO_PATH} type="video/mp4" />
          </video>
        )}
      </motion.div>

      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      </motion.div>

      <div className="relative z-10 h-full">
        <div className="container-tessa flex h-full flex-col justify-center pt-20">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8, ease: EASE }}
              className="mb-6 inline-block font-sans text-[11px] uppercase tracking-[0.28em] text-[#C4A46B] md:text-xs"
              data-testid="hero-eyebrow"
            >
              {t("hero.eyebrow")}
            </motion.span>

            <h1 className="font-serif text-[clamp(2.6rem,7vw,5.5rem)] font-medium leading-[1.02] tracking-tight text-[#F6F2EC]">
              {lines.map((line, index) => (
                <span key={line} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    custom={index}
                    variants={maskLine}
                    initial="hidden"
                    animate="visible"
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8, ease: EASE }}
              className="mt-8 max-w-xl text-base leading-relaxed text-[#F6F2EC]/80 md:text-lg"
              data-testid="hero-description"
            >
              {t("hero.description")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8, ease: EASE }}
              className="mt-10 flex flex-col gap-4 sm:flex-row"
            >
              <button
                onClick={() => scrollTo("#products")}
                data-testid="hero-explore-btn"
                className="group inline-flex h-14 items-center justify-center gap-2 bg-[#F6F2EC] px-8 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#252525] transition-colors hover:bg-white"
              >
                {t("hero.ctaPrimary")}
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
              <button
                onClick={() => scrollTo("#contact")}
                data-testid="hero-partner-btn"
                className="inline-flex h-14 items-center justify-center border border-[#F6F2EC]/40 px-8 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#F6F2EC] transition-colors hover:border-[#D92A47] hover:bg-[#D92A47]"
              >
                {t("hero.ctaSecondary")}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
